import React, { useState } from 'react';
import { AthleteProfile, AppNotification, SportType } from '../types';
import { Bell, Moon, Sun, ShieldCheck, Trophy, Sparkles, UserCheck, LogOut, ChevronDown, Activity, Gift, RefreshCw, Loader2 } from 'lucide-react';
import { VALID_SPORTS, validateAndSanitizeSport } from '../context/SportFilterContext';

interface NavbarProps {
  user?: AthleteProfile | null;
  isLoggedIn?: boolean;
  isDarkMode: boolean;
  setIsDarkMode: (val: boolean) => void;
  isOnline?: boolean;
  setIsOnline?: (val: boolean) => void;
  pendingSyncCount?: number;
  notifications: AppNotification[];
  onOpenNotifications: () => void;
  onOpenAuth: () => void;
  onOpenSignUp: () => void;
  onOpenProfile: () => void;
  onOpenReferralModal?: () => void;
  onNavigateAuth?: () => void;
  onLogout?: () => void;
  activeSportFilter?: SportType;
  isFiltering?: boolean;
  onSportFilterChange?: (sport: SportType) => void;
}

const SPORT_OPTIONS: { id: SportType; label: string; icon: string }[] = [
  { id: 'basketball', label: 'Basketball', icon: '🏀' },
  { id: 'volleyball', label: 'Volleyball', icon: '🏐' },
  { id: 'soccer', label: 'Soccer', icon: '⚽' },
  { id: 'baseball', label: 'Baseball', icon: '⚾' },
  { id: 'softball', label: 'Softball', icon: '🥎' },
  { id: 'pickleball', label: 'Pickleball', icon: '🏓' },
  { id: 'football', label: 'Football', icon: '🏈' },
  { id: 'tennis', label: 'Tennis', icon: '🎾' },
];

export const Navbar: React.FC<NavbarProps> = ({
  user,
  isLoggedIn = true,
  isDarkMode,
  setIsDarkMode,
  isOnline,
  setIsOnline,
  pendingSyncCount,
  notifications,
  onOpenNotifications,
  onOpenAuth,
  onOpenSignUp,
  onOpenProfile,
  onOpenReferralModal,
  onNavigateAuth,
  onLogout,
  activeSportFilter = 'volleyball',
  isFiltering = false,
  onSportFilterChange,
}) => {
  const [isSportDropdownOpen, setIsSportDropdownOpen] = useState(false);
  const validatedSport = validateAndSanitizeSport(activeSportFilter, 'volleyball');
  const unreadCount = notifications.filter((n) => !n.read && !n.cleared).length;
  const currentSportObj = SPORT_OPTIONS.find((s) => s.id === validatedSport) || SPORT_OPTIONS[0];

  return (
    <header className="sticky top-0 z-40 w-full backdrop-blur-md bg-indigo-950/95 dark:bg-indigo-950/95 border-b border-white/10 text-white transition-colors overflow-x-clip max-w-full">
      <div className="max-w-7xl mx-auto px-2.5 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between min-w-0">
        {/* Brand Logo & Title */}
        <button 
          onClick={onOpenProfile}
          className="flex items-center space-x-1.5 sm:space-x-3 shrink-0 text-left focus:outline-none"
        >
          <div className="bg-lime-400 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-xl text-black font-black italic text-xs sm:text-lg tracking-tighter shadow-md shadow-lime-400/20">
            <span className="hidden sm:inline">PLAYGROUND</span>
            <span className="sm:hidden">PLAYGROUND</span>
          </div>
          <div>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <span className="font-black italic text-xs sm:text-lg tracking-tight text-white">
                <span className="hidden xs:inline sm:inline">LEAGUE</span><span className="text-lime-400">.PRO</span>
              </span>
            </div>
            <p className="text-[11px] text-indigo-200/70 font-semibold hidden sm:block">
              High School & College Athlete Stats & Pickup
            </p>
          </div>
        </button>

        {/* Primary Sport Quick-Switch Filter Dropdown with Visual Loading Indicator */}
        <div className="relative mx-1 sm:mx-2 shrink-0">
          <button
            type="button"
            onClick={() => setIsSportDropdownOpen(!isSportDropdownOpen)}
            className={`relative flex items-center space-x-1.5 px-2.5 py-1.5 bg-indigo-900/80 hover:bg-indigo-800 text-lime-300 border rounded-xl shadow-md text-xs font-black italic uppercase transition duration-200 hover:scale-[1.02] active:scale-95 ${
              isFiltering
                ? 'border-lime-400 ring-2 ring-lime-400/40 shadow-lime-400/20 animate-pulse'
                : 'border-lime-400/40'
            }`}
            title="Quick-switch primary sport filter across stats, tournaments & leaderboards"
          >
            {isFiltering ? (
              <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-lime-400 animate-spin shrink-0" />
            ) : (
              <span className="text-sm sm:text-base shrink-0">{currentSportObj.icon}</span>
            )}
            
            <span className="hidden sm:inline tracking-tight">{currentSportObj.label}</span>

            {isFiltering && (
              <span className="hidden md:inline-block text-[9px] bg-lime-400 text-black px-1.5 py-0.5 rounded font-mono font-bold tracking-tighter animate-pulse">
                SYNCING
              </span>
            )}

            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isSportDropdownOpen ? 'rotate-180 text-lime-400' : 'text-indigo-200'}`} />
          </button>

          {isSportDropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsSportDropdownOpen(false)}
              />
              <div className="absolute left-0 mt-2 w-52 bg-indigo-950 border border-lime-400/40 rounded-2xl shadow-2xl z-50 p-2 space-y-1.5 animate-fadeIn">
                <div className="px-2.5 py-1 border-b border-white/10 text-[9px] font-mono font-black uppercase text-lime-400 flex items-center justify-between">
                  <div className="flex items-center space-x-1.5">
                    {isFiltering ? (
                      <RefreshCw className="w-3 h-3 text-lime-400 animate-spin" />
                    ) : (
                      <Activity className="w-3 h-3 text-lime-400" />
                    )}
                    <span>FILTER APP BY SPORT</span>
                  </div>
                  {isFiltering && (
                    <span className="text-[8px] bg-lime-400/20 text-lime-300 px-1 rounded animate-pulse">
                      Updating...
                    </span>
                  )}
                </div>

                <div className="space-y-1 max-h-64 overflow-y-auto custom-scrollbar">
                  {SPORT_OPTIONS.map((sport) => {
                    const isSelected = validatedSport === sport.id;
                    return (
                      <button
                        key={sport.id}
                        type="button"
                        onClick={() => {
                          onSportFilterChange?.(sport.id);
                          setIsSportDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-xl text-xs font-black italic uppercase flex items-center justify-between transition group ${
                          isSelected
                            ? 'bg-lime-400 text-black shadow-md'
                            : 'text-indigo-100 hover:bg-indigo-900 hover:text-white'
                        }`}
                      >
                        <span className="flex items-center space-x-2">
                          <span className="text-base group-hover:scale-110 transition-transform">{sport.icon}</span>
                          <span>{sport.label}</span>
                        </span>
                        {isSelected ? (
                          <div className="flex items-center space-x-1">
                            {isFiltering && <Loader2 className="w-2.5 h-2.5 text-black animate-spin" />}
                            <span className="text-[9px] bg-black text-lime-400 font-bold px-1.5 py-0.5 rounded font-mono">ACTIVE</span>
                          </div>
                        ) : null}
                      </button>
                    );
                  })}
                </div>

                <div className="pt-1 border-t border-white/5 text-[9px] text-indigo-300/60 text-center font-medium">
                  Instant real-time sync across widgets
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right Action Controls */}
        <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-4 shrink-0">
          {/* Refer a Friend Button */}
          {isLoggedIn && onOpenReferralModal && (
            <button
              onClick={onOpenReferralModal}
              className="p-2 bg-gradient-to-r from-amber-400 to-yellow-300 hover:from-amber-300 hover:to-yellow-200 text-black rounded-xl shadow-md transition transform hover:scale-105 active:scale-95 flex items-center justify-center shrink-0"
              title="Refer a Friend & Earn +150 XP"
              aria-label="Refer a Friend"
            >
              <Gift className="w-4 h-4 stroke-[2.5]" />
            </button>
          )}

          {/* Level & XP Pill */}
          {isLoggedIn && user && (
            <button
              onClick={onOpenProfile}
              className="hidden sm:flex items-center space-x-2 bg-indigo-900/60 hover:bg-indigo-900 px-3 py-1.5 rounded-full border border-white/10 transition"
            >
              <div className="w-5 h-5 rounded-full bg-lime-400 text-black flex items-center justify-center font-black text-xs">
                <Trophy className="w-3 h-3" />
              </div>
              <span className="text-xs font-black italic text-lime-400 uppercase">
                LVL {user.level || 1}
              </span>
              <div className="w-16 bg-indigo-950 rounded-full h-2 overflow-hidden p-0.5 border border-white/10">
                <div
                  className="bg-lime-400 h-full rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(100, Math.round(((user.xp || 0) / (user.xpToNextLevel || 1000)) * 100))}%` }}
                />
              </div>
            </button>
          )}

          {/* Notifications Drawer Toggle */}
          {isLoggedIn && (
            <button
              onClick={onOpenNotifications}
              className="relative p-1.5 sm:p-2 rounded-xl text-indigo-200 hover:text-white hover:bg-indigo-900/60 transition"
              aria-label="Notifications"
            >
              <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-rose-500 text-white text-[9px] sm:text-[10px] font-black rounded-full flex items-center justify-center shadow-sm">
                  {unreadCount}
                </span>
              )}
            </button>
          )}

          {/* Dark Mode Toggle */}
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="p-1.5 sm:p-2 rounded-xl text-indigo-200 hover:text-white hover:bg-indigo-900/60 transition"
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-lime-400" /> : <Moon className="w-4 h-4 sm:w-5 sm:h-5 text-amber-300" />}
          </button>

          {/* User Profile Avatar / Login */}
          <div className="flex items-center space-x-1 sm:space-x-2 pl-1 sm:pl-2 border-l border-white/10">
            {isLoggedIn && user ? (
              <>
                <button
                  onClick={onOpenProfile}
                  className="flex items-center p-0.5 rounded-full hover:ring-2 hover:ring-lime-400 transition group"
                  title="View Profile"
                >
                  <div className="relative">
                    <img
                      src={user.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=300'}
                      alt={user.name || 'Athlete'}
                      className="w-7 h-7 sm:w-9 sm:h-9 rounded-full object-cover ring-2 ring-rose-500/80"
                      referrerPolicy="no-referrer"
                    />
                    {user.mfaEnabled && (
                      <span className="absolute -bottom-0.5 -right-0.5 bg-lime-400 text-black rounded-full p-0.5 font-bold" title="MFA Verified Account">
                        <ShieldCheck className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      </span>
                    )}
                  </div>
                </button>

                {onLogout && (
                  <button
                    onClick={onLogout}
                    className="flex items-center space-x-1 px-2 py-1 sm:px-2.5 sm:py-1.5 rounded-xl text-[11px] sm:text-xs font-black uppercase italic bg-rose-500/20 hover:bg-rose-500 hover:text-white border border-rose-500/40 text-rose-300 transition"
                    title="Log Out of Athlete Account"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">LOG OUT</span>
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={onOpenSignUp}
                  className="flex items-center space-x-1 px-2 py-1 sm:px-3 sm:py-1.5 rounded-xl text-[10px] sm:text-xs font-black uppercase italic bg-lime-400 hover:bg-lime-300 text-black transition shadow-md shadow-lime-400/20"
                  title="Open Public Registration Page"
                >
                  <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  <span className="hidden xs:inline">REGISTER</span>
                  <span className="xs:hidden">REG</span>
                </button>

                <button
                  onClick={onOpenAuth}
                  className="flex items-center space-x-1 px-2 py-1 sm:px-3 sm:py-1.5 rounded-xl text-[10px] sm:text-xs font-black uppercase italic bg-indigo-900 hover:bg-indigo-800 border border-white/10 text-indigo-200 transition"
                  title="Open Public Sign In Page"
                >
                  <UserCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  <span className="hidden xs:inline">SIGN IN</span>
                  <span className="xs:hidden">LOGIN</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
